import { CobaltButton } from '@cae/cobalt-react/components/button';
import * as React from 'react';

import styles from './FeedbackContent.module.css';
import { FeedbackContentProps } from './FeedbackContent.types';

/**
 * Purely presentational feedback content component.
 *
 * No router dependency — callers provide onPrimaryAction/onSecondaryAction callbacks.
 * Used by ErrorPage, SessionExpiredError, and ErrorBoundary.
 *
 * Flexible actions:
 * - Primary button: Controlled by onPrimaryAction (hidden if not provided)
 * - Secondary button: Controlled by secondaryActionLabel + onSecondaryAction + showSecondaryAction
 */
const FeedbackContent = React.memo(
  ({
    title,
    titleRef,
    titleSize = 'heading-02',
    message,
    illustration,
    children,
    primaryActionLabel = 'Go to Homepage',
    secondaryActionLabel,
    onPrimaryAction,
    onSecondaryAction,
    showPrimaryAction = true,
    showSecondaryAction = true,
  }: Readonly<FeedbackContentProps>): React.ReactElement => {
    const showPrimaryButton = showPrimaryAction && Boolean(onPrimaryAction);
    const showSecondaryButton =
      showSecondaryAction &&
      Boolean(secondaryActionLabel) &&
      Boolean(onSecondaryAction);

    return (
      <div className={styles.feedbackContent} data-testid="feedback-content">
        <div
          className={styles.illustration}
          data-testid="feedback-illustration"
        >
          {illustration}
        </div>

        <div className={styles.textContent}>
          <h1
            ref={titleRef}
            tabIndex={titleRef ? -1 : undefined}
            className={`${titleSize} ${styles.title}`}
            data-testid="feedback-title"
          >
            {title}
          </h1>
          <p
            className={`body-02 ${styles.message}`}
            data-testid="feedback-message"
          >
            {message}
          </p>
        </div>

        {children}

        {(showPrimaryButton || showSecondaryButton) && (
          <div className={styles.actions} data-testid="feedback-actions">
            {showPrimaryButton && (
              <CobaltButton
                type="button"
                variant="primary"
                onClick={onPrimaryAction}
              >
                {primaryActionLabel}
              </CobaltButton>
            )}
            {showSecondaryButton && (
              <CobaltButton
                type="button"
                variant="ghost"
                onClick={onSecondaryAction}
              >
                {secondaryActionLabel}
              </CobaltButton>
            )}
          </div>
        )}
      </div>
    );
  }
);

FeedbackContent.displayName = 'FeedbackContent';

export { FeedbackContent };
